import { ChangeEvent, MouseEvent, useEffect, useMemo, useState } from "react";
import { DeliveryMethod } from "../../stores/index.d";
import {
  currentDateString,
  formatDate,
  getAsapTime,
  getShopWorkingHours,
  getStartMins,
  isDateToday,
} from "../../utils/functions";
import { IAddress } from "../../stores/cart";
import moment from "moment";
import { useAuth } from "../../contexts/auth-context";
import { useStores } from "../../stores/root-store";
import { observer } from "mobx-react";
import { toInteger } from "lodash";
import { useShopContext } from "../../contexts/shop-context";
import { faClock } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface IAddressSection {
  deliveryMethod: DeliveryMethod;
  onAddressChange: (isAddressFormValid: boolean, addressData: IAddress) => void;
  onOrderDateTimeChange: (
    isDateTimeValid: boolean,
    orderDateTime: {
      date: string;
      time: string;
    }
  ) => void;
}

const AddressSection = observer(
  ({
    onAddressChange,
    onOrderDateTimeChange,
    deliveryMethod,
  }: IAddressSection) => {
    const { userId } = useAuth();
    const {
      shopStartHour,
      shopEndHour,
      shopOpenHoursInterval,
      allowFutureDateOrders,
      estimatedTimeForDelivery,
      estimatedTimeForCollection,
    } = useShopContext();
    const { userStore, cartStore } = useStores();
    const { getUserAddress, userAddress } = userStore;
    const { orderDateTime, deliveryPostcode, setOrderTypeDetailsFromStorage } =
      cartStore;
    const [address, setAddress] = useState<IAddress>({
      fullName: "",
      phoneNumber: "",
      postcode: deliveryPostcode ?? "",
      addressLine1: "",
      addressLine2: "",
      city: "",
    });
    const [orderWantedDateTime, setOrderWantedDateTime] = useState({
      date: orderDateTime?.date ?? currentDateString("YYYY-MM-DD"),
      time: orderDateTime?.time ?? shopStartHour.toString(),
    });
    const [showLoadAddressBtn, setShowLoadAddressBtn] = useState(false);

    useEffect(() => {
      setOrderTypeDetailsFromStorage();
    }, []);

    useEffect(() => {
      if (!orderWantedDateTime?.time || orderWantedDateTime?.time == "0") {
        const asapTime = getAsapTime(
          deliveryMethod == DeliveryMethod.Delivery
            ? estimatedTimeForDelivery
            : estimatedTimeForCollection
        );
        setOrderWantedDateTime((prev) => ({ ...prev, ["time"]: asapTime }));
      }
    }, [
      orderWantedDateTime,
      deliveryMethod,
      estimatedTimeForDelivery,
      estimatedTimeForCollection,
    ]);

    useEffect(() => {
      onOrderDateTimeChange(
        orderWantedDateTime &&
          orderWantedDateTime.date &&
          orderWantedDateTime.time
          ? true
          : false,
        orderWantedDateTime
      );
    }, [orderWantedDateTime]);

    useEffect(() => {
      if (deliveryMethod == DeliveryMethod.Delivery) {
        onAddressChange(
          address.fullName &&
            address.phoneNumber &&
            address.postcode &&
            address.addressLine1 &&
            address.city
            ? true
            : false,
          address
        );
      }
    }, [address, deliveryMethod]);

    useEffect(() => {
      if (deliveryMethod == DeliveryMethod.Collection) return;

      if (userAddress) {
        setAddress(userAddress);
        if (deliveryPostcode)
          setAddress((prev) => ({ ...prev, ["postcode"]: deliveryPostcode }));
        setShowLoadAddressBtn(false);
      } else if (userId && !userAddress) getUserAddress(userId);
    }, [userId, userAddress, deliveryMethod]);

    const handleInputChange = (evt: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = evt.target;
      setAddress((prev) => ({ ...prev, [name]: value }));
    };

    const handleTexAreatChange = (evt: ChangeEvent<HTMLTextAreaElement>) => {
      const { name, value } = evt.target;
      setAddress((prev) => ({ ...prev, [name]: value }));
    };

    // const handleDateChange = (date: string) => {
    //   var selected = moment(date);
    //   var yest = moment().subtract(1, "days");
    //   var isValid = selected.isAfter(yest);
    //   if (isValid) {
    //     setOrderWantedDateTimee((prev) => ({ ...prev, ["date"]: date }));
    //   }
    // };

    const handlHourChange = (hour: string) => {
      setOrderWantedDateTime((prev) => ({ ...prev, ["time"]: hour }));
      if (hour == shopEndHour)
        setOrderWantedDateTime((prev) => ({ ...prev, ["time"]: "00" }));
    };

    const handleLoadAddress = (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (userAddress) setAddress(userAddress);
      if (deliveryPostcode)
        setAddress((prev) => ({ ...prev, ["postcode"]: deliveryPostcode }));
      return false;
    };

    const hours = useMemo(() => {
      return getShopWorkingHours(
        shopStartHour,
        shopEndHour,
        shopOpenHoursInterval,
        orderWantedDateTime.date,
        deliveryMethod == DeliveryMethod.Delivery
          ? estimatedTimeForDelivery
          : estimatedTimeForCollection
      );
    }, [
      shopStartHour,
      shopEndHour,
      shopOpenHoursInterval,
      orderWantedDateTime,
      estimatedTimeForDelivery,
      estimatedTimeForCollection,
      deliveryMethod,
    ]);

    return (
      <div className="row">
        <div className="col-sm-12">
          <div className="panel panel-default">
            <div className="panel-body">
              <form autoComplete="off">
                <h6>Order Wanted Date & Time</h6>
                <div className="row mb-3">
                  <div className="d-flex flex-row align-items-center">
                    <div>
                      <strong className="input-group me-5">
                        {orderWantedDateTime.date &&
                        isDateToday(orderDateTime.date)
                          ? "Today"
                          : formatDate(orderDateTime.date, "dddd, DD MMM yy")}
                      </strong>
                    </div>

                    <div>
                      <div className="input-group">
                        <label
                          className="input-group-text bg-secondary text-white"
                          htmlFor="collectionTimeHour"
                        >
                          <FontAwesomeIcon icon={faClock} />
                        </label>
                        <select
                          className="form-control form-control-lg"
                          id="collectionTimeHour"
                          aria-placeholder="Hour"
                          value={orderWantedDateTime.time}
                          disabled={!orderWantedDateTime.date}
                          onChange={(e) => handlHourChange(e.target.value)}
                        >
                          {hours?.map((h) => (
                            <option key={h.value} value={h.value}>
                              {h.text}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
              {deliveryMethod == DeliveryMethod.Delivery && (
                <form autoComplete="off">
                  <h6 className="mb-3">
                    Delivery Address{" "}
                    {showLoadAddressBtn && (
                      <button
                        className="btn btn-sm btn-secondary float-end rounded-5"
                        onClick={handleLoadAddress}
                      >
                        Load saved address
                      </button>
                    )}
                  </h6>
                  <div className="form-floating mb-2">
                    <input
                      type="text"
                      className="form-control"
                      id="fnU"
                      name="fullName"
                      placeholder="Full Name"
                      required
                      value={address.fullName}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="fnU">Full name</label>
                  </div>
                  <div className="row mb-2">
                    <div className="col">
                      <div className="form-floating">
                        <input
                          type="tel"
                          className="form-control"
                          id="phone"
                          name="phoneNumber"
                          placeholder="Phone No."
                          value={address.phoneNumber}
                          onChange={handleInputChange}
                        />
                        <label htmlFor="phone">Phone No.</label>
                      </div>
                    </div>
                    <div className="col">
                      <div className="form-floating">
                        <input
                          type="text"
                          className="form-control"
                          id="postcode"
                          placeholder="Post Code"
                          name="postcode"
                          value={address.postcode}
                          onChange={handleInputChange}
                        />
                        <label htmlFor="postcode">Post Code</label>
                      </div>
                    </div>
                  </div>

                  <div className="form-floating mb-2">
                    <textarea
                      className="form-control"
                      id="addressLine1"
                      placeholder="Address Line 1"
                      name="addressLine1"
                      value={address.addressLine1}
                      rows={4}
                      onChange={handleTexAreatChange}
                    />
                    <label htmlFor="addressLine1">Address Line 1</label>
                  </div>
                  <div className="form-floating mb-2">
                    <textarea
                      className="form-control"
                      id="addressLine2"
                      placeholder="Address Line 2"
                      name="addressLine2"
                      value={address.addressLine2}
                      rows={4}
                      onChange={handleTexAreatChange}
                    />
                    <label htmlFor="addressLine3">
                      Address Line 2 (Optional)
                    </label>
                  </div>
                  <div className="form-floating mb-3">
                    <input
                      type="text"
                      className="form-control"
                      id="city"
                      placeholder="City"
                      name="city"
                      value={address.city}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="city">City</label>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default AddressSection;
